import { useEffect, useState } from "react";
import { Ban, Copy, Plus, TicketPercent } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

type PromoKind = "email" | "generic";

interface WizardPromo {
  id: string;
  display_hint: string;
  restricted_email: string | null;
  generic_redemption_cap: number | null;
  redemption_count: number;
  credits_total: number;
  starts_at: string;
  ends_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

function promoStatus(promo: WizardPromo) {
  const now = Date.now();
  if (promo.revoked_at) return "Revoked";
  if (new Date(promo.starts_at).getTime() > now) return "Scheduled";
  if (promo.ends_at && new Date(promo.ends_at).getTime() <= now) return "Expired";
  if (promo.generic_redemption_cap && promo.redemption_count >= promo.generic_redemption_cap) return "Redeemed";
  return "Active";
}

export function WizardPromos() {
  const { toast } = useToast();
  const [promos, setPromos] = useState<WizardPromo[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createdCode, setCreatedCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    kind: "email" as PromoKind,
    restrictedEmail: "",
    genericRedemptionCap: "10",
    code: "",
    startsAt: "",
    endsAt: "",
  });

  const invoke = async (action: string, body: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke("admin-wizard-promos", { body: { action, ...body } });
    if (error || data?.error) throw new Error(data?.error || error?.message || "Promo request failed.");
    return data;
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await invoke("list");
      setPromos((data.promos || []).filter((promo: WizardPromo) => !promo.revoked_at));
    } catch (error) {
      toast({ title: "Unable to load wizard promos", description: error instanceof Error ? error.message : "Try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Load once when the admin tab opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, []);

  const copyText = async (text: string, label = "Promo code copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: label });
    } catch {
      toast({ title: "Unable to copy code", description: "Your browser blocked clipboard access.", variant: "destructive" });
    }
  };

  const createPromo = async () => {
    if (form.kind === "email" && !form.restrictedEmail.trim()) {
      toast({ title: "Restricted email required", description: "Enter the email that can redeem this promo.", variant: "destructive" });
      return;
    }
    if (form.kind === "generic") {
      const requestedCap = Number(form.genericRedemptionCap);
      if (!Number.isInteger(requestedCap) || requestedCap <= 0) {
        toast({ title: "Redemption cap required", description: "Generic codes need a positive whole-number cap.", variant: "destructive" });
        return;
      }
    }
    setSaving(true);
    try {
      const data = await invoke("create", {
        kind: form.kind,
        restrictedEmail: form.kind === "email" ? form.restrictedEmail : null,
        genericRedemptionCap: form.kind === "generic" ? Number(form.genericRedemptionCap) : null,
        code: form.code || null,
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
        creditsTotal: 300,
      });
      setCreatedCode(data.code);
      setDialogOpen(false);
      setForm({ kind: "email", restrictedEmail: "", genericRedemptionCap: "10", code: "", startsAt: "", endsAt: "" });
      await load();
    } catch (error) {
      toast({ title: "Unable to create promo", description: error instanceof Error ? error.message : "Try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (promo: WizardPromo) => {
    if (!confirm(`Revoke ${promo.display_hint}? Future signup attempts will stop working.`)) return;
    try {
      await invoke("revoke", { promoId: promo.id });
      setPromos((current) => current.filter((item) => item.id !== promo.id));
      toast({ title: "Promo revoked" });
    } catch (error) {
      toast({ title: "Unable to revoke promo", description: error instanceof Error ? error.message : "Try again.", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Wizard Promos</h2>
          <p className="text-sm text-muted-foreground">Create promo signup campaigns for completed gate-router handoffs.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}><Plus className="mr-2 h-4 w-4" /> Create promo</Button>
      </div>

      {createdCode && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4">
            <p className="text-sm font-semibold">New promo code</p>
            <p className="mt-1 text-xs text-muted-foreground">Admins can copy this code later from the active promo list.</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <code className="max-w-full break-all rounded bg-background px-3 py-2 text-sm">{createdCode}</code>
              <Button variant="outline" size="sm" onClick={() => void copyText(createdCode)}><Copy className="mr-2 h-4 w-4" /> Copy</Button>
              <Button variant="ghost" size="sm" onClick={() => setCreatedCode("")}>Dismiss</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <p className="text-sm text-muted-foreground">Loading promos...</p> : promos.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No wizard promos yet.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {promos.map((promo) => {
            const status = promoStatus(promo);
            return (
              <Card key={promo.id}>
                <CardContent className="flex flex-wrap items-center gap-4 p-4">
                  <TicketPercent className="h-5 w-5 text-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="text-sm font-semibold">{promo.display_hint}</code>
                      <Badge variant="outline">{status}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {promo.restricted_email ? `Restricted to ${promo.restricted_email}` : `Generic cap ${promo.generic_redemption_cap}`} · {promo.redemption_count} redeemed · {promo.credits_total} credits
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => void copyText(promo.display_hint)}><Copy className="mr-2 h-4 w-4" /> Copy</Button>
                    {status === "Active" && <Button size="sm" variant="outline" onClick={() => revoke(promo)}><Ban className="mr-2 h-4 w-4" /> Revoke</Button>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create wizard promo</DialogTitle>
            <DialogDescription>Use a restricted email or set an explicit cap for a generic code.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Campaign type</Label>
              <RadioGroup
                value={form.kind}
                onValueChange={(kind) => setForm({ ...form, kind: kind as PromoKind })}
                className="mt-2 grid gap-2 sm:grid-cols-2"
              >
                <Label
                  htmlFor="wizard-promo-kind-email"
                  className="flex cursor-pointer items-start gap-3 rounded-md border border-input bg-background p-3 text-sm hover:bg-accent/40 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
                >
                  <RadioGroupItem id="wizard-promo-kind-email" value="email" className="mt-0.5" />
                  <span>
                    <span className="block font-medium">Email-restricted</span>
                    <span className="mt-1 block text-xs font-normal text-muted-foreground">Only one matching email can redeem it.</span>
                  </span>
                </Label>
                <Label
                  htmlFor="wizard-promo-kind-generic"
                  className="flex cursor-pointer items-start gap-3 rounded-md border border-input bg-background p-3 text-sm hover:bg-accent/40 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
                >
                  <RadioGroupItem id="wizard-promo-kind-generic" value="generic" className="mt-0.5" />
                  <span>
                    <span className="block font-medium">Generic code</span>
                    <span className="mt-1 block text-xs font-normal text-muted-foreground">Anyone with the code can redeem up to the cap.</span>
                  </span>
                </Label>
              </RadioGroup>
            </div>
            {form.kind === "email" ? (
              <div><Label htmlFor="wizard-promo-email">Restricted email</Label><Input id="wizard-promo-email" type="email" value={form.restrictedEmail} onChange={(event) => setForm({ ...form, restrictedEmail: event.target.value })} /></div>
            ) : (
              <div><Label htmlFor="wizard-promo-cap">Redemption cap</Label><Input id="wizard-promo-cap" type="number" min="1" value={form.genericRedemptionCap} onChange={(event) => setForm({ ...form, genericRedemptionCap: event.target.value })} /></div>
            )}
            <div><Label htmlFor="wizard-promo-code">Custom code (optional)</Label><Input id="wizard-promo-code" value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })} placeholder="Leave blank to generate" /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label htmlFor="wizard-promo-start">Starts at</Label><Input id="wizard-promo-start" type="datetime-local" value={form.startsAt} onChange={(event) => setForm({ ...form, startsAt: event.target.value })} /></div>
              <div><Label htmlFor="wizard-promo-end">Ends at</Label><Input id="wizard-promo-end" type="datetime-local" value={form.endsAt} onChange={(event) => setForm({ ...form, endsAt: event.target.value })} /></div>
            </div>
            <div><Label htmlFor="wizard-promo-credits">Starting credits</Label><Input id="wizard-promo-credits" value="300" disabled /><p className="mt-1 text-xs text-muted-foreground">Wizard promo accounts always start with exactly 300 credits.</p></div>
            <Button className="w-full" disabled={saving} onClick={createPromo}>{saving ? "Creating..." : "Create promo"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
